import React from 'react';
import { useFormContext } from "react-hook-form";
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ToggleRightIcon, ListIcon } from 'lucide-react';
import { useClassificationOptions } from '@/features/alojamientos/hooks/useClassificationOptions';

interface ClassificationSectionProps {
  mode?: "create" | "edit" | "view";
}

const ClassificationSection = ({ mode }: ClassificationSectionProps) => {
  const { setValue, watch, getValues } = useFormContext();
  
  const classId = watch('classId');
  const { typeOptions, categoryOptions, segment } = useClassificationOptions(classId);
  const isTouristComplex = watch('isTouristComplex', false);
  const categoryId = watch('categoryId');

  // 🚫 Evita renderizar antes de que RHF tenga valores cargados
  // Solo validamos en modo edit
  if (mode === "edit" && (classId === undefined || categoryId === undefined)) {
    return null;
  }

  const handleTypeChange = (value: string) => {
    const newTypeId = Number(value);
    
    // Validar que el valor sea un número válido
    if (isNaN(newTypeId) || newTypeId === 0) {
      console.warn('❌ Aborting setValue. Invalid typeId:', newTypeId);
      return;
    }
    
    // Resetear la categoría cuando cambie el tipo
    setValue('categoryId', null, { 
      shouldValidate: true,
      shouldDirty: true
    });
    
    setValue('classId', newTypeId, { 
      shouldValidate: true,
      shouldDirty: true
    });
  };

  const handleCategoryChange = (value: string) => {
    const newCategoryId = Number(value);
    
    // Validar que el valor sea un número válido
    if (isNaN(newCategoryId) || newCategoryId === 0) {
      console.warn('❌ Aborting setValue. Invalid categoryId:', newCategoryId);
      return;
    }
    
    setValue('categoryId', newCategoryId, { 
      shouldValidate: true,
      shouldDirty: true
    });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
      <div className="form-group">
        <Label className="form-label flex items-center gap-2">
          <ToggleRightIcon size={16} />
          Es complejo turístico
        </Label>
        <div className="flex items-center space-x-2">
          <Switch
            checked={isTouristComplex}
            onCheckedChange={(checked) => setValue('isTouristComplex', checked, { 
              shouldValidate: true,
              shouldDirty: true
            })}
          />
          <span>{isTouristComplex ? 'Sí' : 'No'}</span>
        </div>
      </div>

      <div className="form-group">
        <Label className="form-label flex items-center gap-2">
          <ListIcon size={16} />
          Tipo/Clase
        </Label>
        <Select 
          value={classId ? String(classId) : undefined}
          onValueChange={handleTypeChange}
        >
          <SelectTrigger className="form-select">
            <SelectValue placeholder="Seleccionar tipo"/>
          </SelectTrigger>
          <SelectContent>
            {typeOptions.map((type) => (
              <SelectItem key={type.id} value={String(type.id)}>
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="form-group">
        <Label className="form-label flex items-center gap-2">
          <ListIcon size={16} />
          Categoría
        </Label>
        <Select 
          value={categoryId ? String(categoryId) : undefined}
          onValueChange={handleCategoryChange}
          disabled={!classId || !categoryOptions.length}
        >
          <SelectTrigger className="form-select">
            <SelectValue placeholder={!classId ? "Seleccione primero el tipo" : "Seleccionar categoría"}/>
          </SelectTrigger>
          <SelectContent>
            {categoryOptions.map((category) => (
              <SelectItem key={category.id} value={String(category.id)}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="form-group">
        <Label className="form-label flex items-center gap-2">
          <ListIcon size={16} />Segmento</Label>
          <Input 
            className="form-input bg-gray-100"
            value={segment}
            disabled
          />
      </div>
    </div>
  );
};

export default ClassificationSection;
